import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { wizardService } from '../../services/wizardService';
import { menuService } from '../../services/menuService';
import { dashboardService } from '../../services/dashboardService';
import { extractApiError } from '../../services/tenantRegistrationService';
import type { DescriptionCompany } from '../../types/wizard';
import logoCorgee from '../../assets/logos/logo-corgee.png';
import cloudBackground from '../../assets/images/cloud_bg.png';
import './InitialWizard.css';

const INITIAL_CUSTOMIZE_KEY = 'C2_initialCustomize';
const OPEN_HELP_KEY = 'C2_openHelp';
const MAIL_URL_KEY = 'C2_mailUrl';
const TENANCY_NAME_KEY = 'C2_tenancyName';

type WizardStep = 'welcome' | 'description' | 'done';

/**
 * Initial configuration wizard shown on the first login of a new tenant
 * (migrated from the legacy WizardInitial React component).
 *
 * Flow: welcome -> choose the description that best fits the organization ->
 * the API creates the initial configuration (departments become security groups)
 * -> done -> go to Corgee.
 */
export const InitialWizardPage: React.FC = () =>
{
  const navigate = useNavigate();
  const { InitialCustomize } = useAuth();
  const tenancyName = localStorage.getItem(TENANCY_NAME_KEY) || '';

  const [step, setStep] = useState<WizardStep>('welcome');
  const [companies, setCompanies] = useState<DescriptionCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Already configured sites go straight to the app
  useEffect(() =>
  {
    if (InitialCustomize())
    {
      navigate('/', { replace: true });
    }
  }, [InitialCustomize, navigate]);

  useEffect(() =>
  {
    let cancelled = false;
    wizardService
      .getDescriptionCompanies()
      .then(data =>
      {
        if (!cancelled) setCompanies(data);
      })
      .catch(err =>
      {
        if (!cancelled) setError(extractApiError(err, 'Could not load the descriptions.'));
      });
    return () =>
    {
      cancelled = true;
    };
  }, []);

  const selectedCompany = companies.find(c => c.DescriptionCompanyId === selectedCompanyId) || null;

  /**
   * Same sequence as the legacy wizard: persist the selected description,
   * persist its departments (all selected) and create the initial configuration.
   */
  const saveConfiguration = useCallback(async () =>
  {
    if (!selectedCompany) return;

    setLoading(true);
    setError('');
    try
    {
      const company: DescriptionCompany = { ...selectedCompany, TenantName: tenancyName };
      const savedCompanyId =
        (await wizardService.addUpdateDescriptionCompanies([company])) ||
        selectedCompany.DescriptionCompanyId;

      const departments = await wizardService.getDepartmentsByDescriptionCompanyId(
        selectedCompany.DescriptionCompanyId,
      );
      const selectedDepartments = departments.map(department => ({
        ...department,
        DescriptionCompanyId: savedCompanyId,
        Selected: true,
      }));

      const savedDepartments = await wizardService.addUpdateDepartments(selectedDepartments);
      const departmentNames = (savedDepartments.length ? savedDepartments : selectedDepartments)
        .filter(department => department.Selected !== false)
        .map(department => department.Department);

      await wizardService.createConfigurationInitial({
        descriptionCompanyId: savedCompanyId,
        departments: departmentNames.join(','),
        tenantName: tenancyName,
      });

      setStep('done');
    }
    catch (err)
    {
      setError(extractApiError(err, 'The configuration could not be saved. Please try again.'));
    }
    finally
    {
      setLoading(false);
    }
  }, [selectedCompany, tenancyName]);

  /** Legacy fncGoToCorgee: refresh the side menu, mark the site as configured and enter */
  const goToCorgee = useCallback(async () =>
  {
    setLoading(true);
    try
    {
      await menuService.getMenuByPerson();
    }
    catch
    {
      // Menu refresh is best effort; the app loads it again on entry
    }

    localStorage.setItem(INITIAL_CUSTOMIZE_KEY, 'true');

    const mailUrl = localStorage.getItem(MAIL_URL_KEY);
    if (mailUrl)
    {
      window.location.href = mailUrl;
      return;
    }

    localStorage.setItem(OPEN_HELP_KEY, 'true');

    let path = '/Dashboards';
    try
    {
      const betaInfo = await dashboardService.getIsBeta();
      localStorage.setItem('C2_isBeta', String(betaInfo.isBeta));
      path = betaInfo.isBeta ? '/home' : '/Dashboards';
    }
    catch
    {
      path = localStorage.getItem('C2_isBeta') === 'true' ? '/home' : '/Dashboards';
    }
    navigate(path, { replace: true });
  }, [navigate]);

  return (
    <div className="initial-wizard">
      <div
        className="initial-wizard-container"
        style={{ backgroundImage: `url(${cloudBackground})` }}
      >
        {loading && (
          <div className="initial-wizard-loader-bg">
            <div className="initial-wizard-loader" />
          </div>
        )}

        {step === 'welcome' && (
          <div className="initial-wizard-welcome">
            <img src={logoCorgee} alt="Corgee" className="sitelogo-icon" />
            <div className="welcome-title-container">
              <h1>Welcome to Corgee</h1>
              <p>You are the administrator of the</p>
              <p>
                <span>{tenancyName}</span> site.
              </p>
            </div>
            <div className="welcome-footer-container">
              <button
                type="button"
                className="initial-wizard-btn"
                onClick={() => setStep('description')}
              >
                Start Now
              </button>
              <p>Follow this guide to customize Corgee for your organization.</p>
              <p>(You can always make changes later)</p>
            </div>
          </div>
        )}

        {step === 'description' && (
          <div className="initial-wizard-section">
            <h3 className="initial-wizard-section-title">
              Choose the description that best fits your needs. I&apos;m using Corgee to...
            </h3>

            {error && <div className="initial-wizard-error">{error}</div>}

            <div className="initial-wizard-contentbox">
              <div className="initial-wizard-cards">
                {companies.map(company => (
                  <button
                    key={company.DescriptionCompanyId}
                    type="button"
                    className={`initial-wizard-option${
                      company.DescriptionCompanyId === selectedCompanyId ? ' is-selected' : ''
                    }`}
                    onClick={() => setSelectedCompanyId(company.DescriptionCompanyId)}
                    disabled={loading}
                  >
                    <div className="initial-wizard-option-description">
                      <Building2 className="initial-wizard-option-icon" />
                      <h2 dangerouslySetInnerHTML={{ __html: company.DescriptionCompany }} />
                    </div>
                  </button>
                ))}
                {companies.length === 0 && !error && (
                  <p className="initial-wizard-section-description">Loading descriptions...</p>
                )}
              </div>
            </div>

            <div className="initial-wizard-btn-box">
              <button
                type="button"
                className="initial-wizard-btn"
                onClick={saveConfiguration}
                disabled={loading || !selectedCompany}
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="initial-wizard-welcome">
            <img src={logoCorgee} alt="Corgee" className="sitelogo-icon" />
            <div className="welcome-title-container">
              <h1>Your configuration has been set up.</h1>
            </div>
            <div className="welcome-footer-container">
              <button
                type="button"
                className="initial-wizard-btn"
                onClick={goToCorgee}
                disabled={loading}
              >
                Go to Corgee
              </button>
            </div>
          </div>
        )}
      </div>

      {step !== 'welcome' && (
        <div className="initial-wizard-footer">
          <p>Follow this guide to customize Corgee for your organization.</p>
          <p>(You can always make changes later)</p>
        </div>
      )}
    </div>
  );
};

export default InitialWizardPage;
